import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";

/**
 * Structured family details for the Celebrate a Life submission form.
 *
 * value shape (matches backend MemorialIn.family_details):
 * {
 *   spouses[], former_spouses[],
 *   sons[], daughters[], adopted_children[], stepchildren[],
 *   grandchildren[], great_grandchildren[],
 *   father, mother, mother_maiden_name,
 *   brothers[], sisters[],
 *   in_laws[], cousins[], nieces[], nephews[],
 *   close_friends[],
 *   next_of_kin[] (dicts {name, relation, phone, email, city, country})
 * }
 */
const LIST_SECTIONS = [
  { key: "spouses", label: "Spouse(s)" },
  { key: "former_spouses", label: "Former spouse(s)" },
  { key: "sons", label: "Sons" },
  { key: "daughters", label: "Daughters" },
  { key: "adopted_children", label: "Adopted children" },
  { key: "stepchildren", label: "Stepchildren" },
  { key: "grandchildren", label: "Grandchildren" },
  { key: "great_grandchildren", label: "Great-grandchildren" },
  { key: "brothers", label: "Brothers" },
  { key: "sisters", label: "Sisters" },
  { key: "in_laws", label: "In-laws" },
  { key: "cousins", label: "Cousins" },
  { key: "nieces", label: "Nieces" },
  { key: "nephews", label: "Nephews" },
  { key: "close_friends", label: "Close friends" },
];

const border = { borderColor: "var(--wofy-border)" };

export default function FamilyDetailsPanel({ value = {}, onChange, isMarried }) {
  const v = value || {};
  const [showAll, setShowAll] = useState(false);

  const set = (k, val) => onChange({ ...v, [k]: val });
  const addTo = (k) => set(k, [...(v[k] || []), ""]);
  const removeAt = (k, i) => set(k, (v[k] || []).filter((_, j) => j !== i));
  const updateAt = (k, i, val) => {
    const arr = [...(v[k] || [])];
    arr[i] = val;
    set(k, arr);
  };

  const visibleSections = showAll ? LIST_SECTIONS : LIST_SECTIONS.filter((s) => {
    // Only show marital sections when user is married
    if (!isMarried && (s.key === "spouses" || s.key === "former_spouses")) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Parents block */}
      <div>
        <div className="text-[11px] font-mono uppercase tracking-wider mb-2 opacity-80">Parents</div>
        <div className="grid md:grid-cols-3 gap-3">
          <FieldRow label="Father's full name" value={v.father || ""} onChange={(x) => set("father", x)} testid="fam-father" />
          <FieldRow label="Mother's full name" value={v.mother || ""} onChange={(x) => set("mother", x)} testid="fam-mother" />
          <FieldRow label="Mother's maiden name" value={v.mother_maiden_name || ""} onChange={(x) => set("mother_maiden_name", x)} testid="fam-mother-maiden" />
        </div>
      </div>

      {/* Children / grandchildren / siblings / etc. */}
      {visibleSections.map((s) => (
        <ListSection
          key={s.key}
          label={s.label}
          items={v[s.key] || []}
          onAdd={() => addTo(s.key)}
          onRemove={(i) => removeAt(s.key, i)}
          onUpdate={(i, val) => updateAt(s.key, i, val)}
          testidPrefix={`fam-${s.key}`}
        />
      ))}

      {/* Next of kin (rich rows) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-80">Next of kin</div>
          <button type="button" onClick={() => set("next_of_kin", [...(v.next_of_kin || []), { name: "", relation: "", phone: "", email: "", city: "", country: "" }])} className="wofy-btn-ghost text-xs" data-testid="fam-add-nok">
            <Plus className="w-3 h-3" /> Add contact
          </button>
        </div>
        {(v.next_of_kin || []).map((nk, i) => (
          <div key={i} className="grid md:grid-cols-6 gap-2 items-center mb-2">
            <input placeholder="Name" value={nk.name || ""} onChange={(e) => {
              const arr = [...(v.next_of_kin || [])]; arr[i] = { ...nk, name: e.target.value }; set("next_of_kin", arr);
            }} className="rounded border px-2 py-2 text-sm bg-transparent md:col-span-2" style={border} />
            <input placeholder="Relation" value={nk.relation || ""} onChange={(e) => { const arr = [...(v.next_of_kin || [])]; arr[i] = { ...nk, relation: e.target.value }; set("next_of_kin", arr); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border} />
            <input placeholder="Phone" value={nk.phone || ""} onChange={(e) => { const arr = [...(v.next_of_kin || [])]; arr[i] = { ...nk, phone: e.target.value }; set("next_of_kin", arr); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border} />
            <input placeholder="Email" value={nk.email || ""} onChange={(e) => { const arr = [...(v.next_of_kin || [])]; arr[i] = { ...nk, email: e.target.value }; set("next_of_kin", arr); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border} />
            <button type="button" onClick={() => set("next_of_kin", (v.next_of_kin || []).filter((_, j) => j !== i))} className="text-red-500 justify-self-start"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <button type="button" onClick={() => setShowAll((x) => !x)} className="wofy-btn-ghost text-xs" data-testid="fam-toggle-all">
        {showAll ? "Show fewer sections" : "Show all relationship types"}
      </button>
    </div>
  );
}

function ListSection({ label, items, onAdd, onRemove, onUpdate, testidPrefix }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] font-mono uppercase tracking-wider opacity-80">{label}</div>
        <button type="button" onClick={onAdd} className="wofy-btn-ghost text-xs" data-testid={`${testidPrefix}-add`}>
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      {items.length === 0 && <div className="text-[11px] opacity-60">— none —</div>}
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-[1fr_auto] gap-2 items-center mb-1">
          <input value={it || ""} onChange={(e) => onUpdate(i, e.target.value)} placeholder="Name" className="rounded border px-2 py-2 text-sm bg-transparent" style={border} data-testid={`${testidPrefix}-${i}`} />
          <button type="button" onClick={() => onRemove(i)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

function FieldRow({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-mono uppercase tracking-wider">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={border} data-testid={testid} />
    </label>
  );
}
